/**
 * API Keys API Service
 *
 * Handles all API key management-related API calls
 */

import { AI_OPERATIONS_API_URL } from './config';
import { demoModeManager } from './demoModeManager';

const API_BASE_URL = `${AI_OPERATIONS_API_URL}/genaiops`;
const isDemoActive = (): boolean => demoModeManager.getDemoMode().isDemoMode;

/**
 * Get the selected client ID from localStorage
 * Used by partner/aws/admin users who need to specify which client they're viewing
 */
const getSelectedClientId = (): string | null => {
  return localStorage.getItem('selectedClientId');
};

/**
 * Append client_id to URL if available (for partner/aws/admin users)
 */
const appendClientId = (url: string): string => {
  const clientId = getSelectedClientId();
  if (!clientId) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}client_id=${clientId}`;
};

/**
 * Get authorization headers with JWT token
 */
const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
  };
};

export interface APIKeyUpdates {
  name?: string;
  description?: string;
  scopes?: string;
  expires_at?: string | null;
}

/**
 * Create a new API key
 *
 * IMPORTANT: The full API key is ONLY returned in this response.
 * You will NOT be able to retrieve it again!
 */
export const createAPIKey = async (keyData: Record<string, any>): Promise<any> => {
  if (isDemoActive()) return { id: 'demo-key-' + Date.now(), key: 'demo-ak_live_xxxxxxxxxxxx', key_prefix: 'ak_live_xxxx', ...keyData, is_active: true, created_at: new Date().toISOString() };
  const response = await fetch(appendClientId(`${API_BASE_URL}/api-keys`), {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(keyData),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || `Failed to create API key: ${response.statusText}`);
  }

  return response.json();
};

/**
 * List all API keys
 *
 * Note: Full API keys are NOT included. Only prefixes are shown.
 */
export const listAPIKeys = async (includeInactive: boolean = false): Promise<any> => {
  if (isDemoActive()) return [];
  const queryParams = new URLSearchParams({
    include_inactive: includeInactive.toString()
  });

  const response = await fetch(appendClientId(`${API_BASE_URL}/api-keys?${queryParams}`), {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch API keys: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Get a single API key by ID
 *
 * Note: Full API key is NOT included. Only prefix and metadata.
 */
export const getAPIKey = async (apiKeyId: string): Promise<any> => {
  if (isDemoActive()) return { id: apiKeyId, name: 'Demo Key', key_prefix: 'ak_live_xxxx', is_active: true, scopes: 'read,write', created_at: new Date().toISOString() };
  const response = await fetch(appendClientId(`${API_BASE_URL}/api-keys/${apiKeyId}`), {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch API key: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Update an API key's metadata (name, description, scopes, expiration)
 */
export const updateAPIKey = async (apiKeyId: string, updates: APIKeyUpdates): Promise<any> => {
  if (isDemoActive()) return { id: apiKeyId, ...updates };
  const response = await fetch(appendClientId(`${API_BASE_URL}/api-keys/${apiKeyId}`), {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || `Failed to update API key: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Revoke an API key (soft delete)
 *
 * The key will no longer work for authentication.
 */
export const revokeAPIKey = async (apiKeyId: string): Promise<any> => {
  if (isDemoActive()) return { success: true };
  const response = await fetch(appendClientId(`${API_BASE_URL}/api-keys/${apiKeyId}`), {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || `Failed to revoke API key: ${response.statusText}`);
  }

  return response.json();
};
